import { useRef } from 'react'

import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import {
  AtsCandidateEmailActionTypesEnum,
  AtsCreateCandidateEmailActionDocument,
  AtsCreateCandidateEmailActionMutation,
  AtsGetJobPostingSettingsSmartActionsDocument,
  AtsJobPostingSettingsNewSmartActionFragment,
} from 'generated/resources/graphql'
import { isEmpty, isEqual, omitBy, pickBy, xor } from 'lodash'
import { useToggle } from 'usehooks-ts'

import Select from 'modules/core/components/Select'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { useMessageOptions } from 'modules/ats/lib/utils/messages'

import { ApplicationPhasesSelector, useGetOrderedPhases } from './ApplicationPhasesSelector'
import { MissingFieldsAlert } from './MissingFieldsAlert'
import { FormValues } from './forms'

interface Props {
  title: string
  open: boolean
  onClose: () => void
  jobId: string
  smartActions: AtsJobPostingSettingsNewSmartActionFragment[]
  actionType: AtsCandidateEmailActionTypesEnum
}

export const NewSmartAction = ({
  title,
  open,
  onClose,
  jobId,
  smartActions,
  actionType,
}: Props) => {
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const [createSmartAction, { loading }] = useMutation(AtsCreateCandidateEmailActionDocument, {
    extractorFn: (data: AtsCreateCandidateEmailActionMutation) =>
      data.atsMutations.createAtsCandidateEmailAction,
    refetchQueries: [AtsGetJobPostingSettingsSmartActionsDocument],
  })
  const { company } = useCurrentSession()
  const { defaultOption, fallbackOption, sendMessageOptions } = useMessageOptions()

  const { orderedPhases } = useGetOrderedPhases({
    jobId: jobId,
    actionType: actionType,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      atsJobPostingId: jobId,
      messageContent: '',
      atsApplicationPhaseIds: [],
      actionType,
      delay: 'now',
      enabled: true,
      emailFrom: 'conversation',
      files: [],
    },
    validate: (data) => {
      const formErrors: FormErrors<FormValues> = {}

      if (data.atsApplicationPhaseIds.length === 0) {
        formErrors.atsApplicationPhaseIds = i18n.t('common.validations.empty')
      }

      if (!data.messageContent) {
        formErrors.messageContent = i18n.t('common.validations.empty')
      }

      return formErrors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createSmartAction({
        variables: {
          actionType: data.actionType,
          atsApplicationPhaseIds: data.atsApplicationPhaseIds,
          atsJobPostingId: data.atsJobPostingId,
          delay: data.delay,
          emailFrom: data.emailFrom,
          enabled: data.enabled,
          messageContent: data.messageContent,
          files: data.files,
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        const selectedPhases = orderedPhases.filter((phase) =>
          data.atsApplicationPhaseIds.includes(phase.id)
        )

        track(trackingEvents.ats.smartActionCreate, {
          company_id: company.id,
          action_type: actionType,
          stage_id: selectedPhases.map((phase) => phase.hiringStage?.id),
          stage_name: selectedPhases.map((phase) => phase.hiringStage?.name || 'Unassigned'),
        })

        onClose()
        form.resetForm()
      }

      return handled
    },
  })

  const [delayOptionChecked, toggleDelayOptionChecked] = useToggle(
    sendMessageOptions.some((option) => option.value === form.values.delay)
  )

  const handleOnCheckboxDisable = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleDelayOptionChecked()
    !event.target.checked && form.setFieldValue('delay', fallbackOption.value)
  }

  const setFieldTouched = (field: string) => {
    if (form.getFieldMeta(field).touched) return

    form.setFieldTouched(field)
  }

  const onSelectPhases = (phaseId: string) => {
    setFieldTouched('atsApplicationPhaseIds')
    form.setFieldValue('atsApplicationPhaseIds', xor(form.values.atsApplicationPhaseIds, [phaseId]))
  }

  const onFiles = useUnsafeCallback(
    (files) => {
      if (isEqual(files, form.values.files)) {
        return
      }
      form.setFieldValue('files', files, false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.values.files.length]
  )

  const sendAsCorporate = form.values.emailFrom !== 'conversation'

  const usedApplicationPhases = smartActions.flatMap((action) => {
    return action.atsApplicationPhases.map((phase) => phase.id)
  })

  const errors = omitBy(form.errors, (_value, key) => !Object.keys(form.touched).includes(key))

  const formErrors = pickBy(errors, (field) => !isEmpty(field))

  const selectPhaseLabel =
    actionType === 'reject'
      ? i18n.t(
          'ats.job_posting_settings.smart_actions.sidepanel.rejection_emails_select_phase_label'
        )
      : i18n.t('ats.job_posting_settings.smart_actions.sidepanel.phase_changed_select_phase_label')

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={title}
      closeLabel={i18n.t('common.close')}
      width="wide"
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={() => form.handleSubmit()} loading={loading}>
            {i18n.t('common.create')}
          </Button>
        </Box>
      }
    >
      <Box gap="s32">
        {!!Object.keys(formErrors).length && <MissingFieldsAlert formErrors={formErrors} />}

        <Box maxWidth="s400" gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {selectPhaseLabel}
          </Text.Normal>

          <ApplicationPhasesSelector
            onSelectPhase={(phaseId) => onSelectPhases(String(phaseId))}
            disabledApplicationPhaseIds={usedApplicationPhases}
            checkedApplicationPhaseIds={form.values.atsApplicationPhaseIds}
            jobId={jobId}
            actionType={actionType}
          />
        </Box>

        <Box gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t('ats.job_posting_settings.smart_actions.sidepanel.send_email_from')}
          </Text.Normal>

          <Select
            value={form.values.emailFrom}
            onChange={(_value) =>
              form.setFieldValue('emailFrom', sendAsCorporate ? 'conversation' : 'no_reply')
            }
            options={[
              {
                label: i18n.t('ats.job_posting_settings.smart_actions.sender_options.conversation'),
                value: 'conversation',
              },
              {
                label: i18n.t('ats.job_posting_settings.smart_actions.sender_options.no_reply'),
                value: 'no_reply',
              },
            ]}
          />
        </Box>
        <Box gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t(
              'ats.job_posting_settings.smart_actions.sidepanel.containing_following_message'
            )}
          </Text.Normal>
          <TextEditorWithMessageTemplates
            richTextEditorRef={richTextEditorRef}
            onChange={(value) => {
              setFieldTouched('messageContent')
              form.setFieldValue('messageContent', value)
            }}
            placeholder={i18n.t(
              'ats.job_posting_settings.smart_actions.sidepanel.message_placeholder'
            )}
            onFiles={onFiles}
            value={form.values.messageContent}
            showLabel={false}
          />
          <Box paddingTop="s8" width="full" gap="s16">
            <Box gap="s8">
              <Checkbox
                size="medium"
                label={i18n.t('ats.job_posting_settings.smart_actions.sidepanel.delay')}
                checked={delayOptionChecked}
                onChange={(event) => handleOnCheckboxDisable(event)}
              />
              <Text.Normal size="200" color="textSecondary">
                {i18n.t('ats.job_posting_settings.smart_actions.sidepanel.delay_warn')}
              </Text.Normal>
            </Box>
            <Select
              hideLabel
              defaultOption={defaultOption}
              readonly={!delayOptionChecked}
              options={sendMessageOptions.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              onChange={(value) => form.setFieldValue('delay', value)}
              value={form.values.delay}
            />
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
